import { FormEvent, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Server, Plug, Trash2, Download, Images, ListTree } from "lucide-react";
import { Link } from "react-router-dom";
import {
  connectorsAPI,
  Connector,
  ConnectorCollectionField,
  ConnectorFieldTypeOverrides,
} from "../api/connectorsApi";

const CONNECTOR_FIELD_TYPE_OPTIONS = [
  "text",
  "textarea",
  "richtext",
  "number",
  "checkbox",
  "date",
  "datetime",
  "dateonly",
  "timeonly",
  "select",
  "email",
  "url",
  "image",
  "file",
  "instruction",
  "code",
  "gpx",
] as const;

/**
 * Admin page for external-server connectors.
 *
 * Secrets (dtoken) are write-only: the form never receives the stored value, only
 * a "configured / not set" badge, and submitting a blank token leaves it unchanged.
 * Imported remote collections appear in the normal collection list; which servers
 * and which remote collections a user can access is configured in the ACL admin.
 */
export function ConnectorsPage() {
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [supportedTypes, setSupportedTypes] = useState<string[]>(["dtoken-cms"]);
  const [encryptionConfigured, setEncryptionConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await connectorsAPI.list();
      setConnectors(res.connectors);
      setSupportedTypes(res.supportedTypes?.length ? res.supportedTypes : ["dtoken-cms"]);
      setEncryptionConfigured(res.encryptionConfigured);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load connectors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const flash = (msg: string) => {
    setSuccess(msg);
    setError(null);
    setTimeout(() => setSuccess(null), 4000);
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Plug className="w-5 h-5 mr-2" /> External Connectors
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!encryptionConfigured && (
            <Alert variant="destructive">
              <AlertDescription>
                CONNECTOR_SECRET_KEY is not configured on the server. You can create
                connectors, but secrets (tokens) cannot be stored until it is set.
              </AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert>
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          <CreateConnectorForm
            supportedTypes={supportedTypes}
            onCreated={() => {
              void load();
              flash("Connector created");
            }}
            onError={setError}
          />
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm text-gray-500">Loading connectors…</p>
      ) : connectors.length === 0 ? (
        <p className="text-sm text-gray-500">No connectors configured yet.</p>
      ) : (
        connectors.map((c) => (
          <ConnectorCard
            key={c.uid}
            connector={c}
            supportedTypes={supportedTypes}
            onChanged={(msg) => {
              void load();
              if (msg) flash(msg);
            }}
            onError={setError}
          />
        ))
      )}
    </div>
  );
}

function CreateConnectorForm({
  supportedTypes,
  onCreated,
  onError,
}: {
  supportedTypes: string[];
  onCreated: () => void;
  onError: (msg: string) => void;
}) {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [type, setType] = useState(supportedTypes[0] || "dtoken-cms");
  const [collections, setCollections] = useState("");
  const [dtoken, setDtoken] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await connectorsAPI.create({
        key: key.trim(),
        label: label.trim() || key.trim(),
        type,
        baseUrl: baseUrl.trim(),
        config: { collections: parseCollections(collections) },
        dtoken: dtoken.trim() || undefined,
      });
      setKey(""); setLabel(""); setBaseUrl(""); setCollections(""); setDtoken("");
      onCreated();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to create connector");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3 md:grid-cols-2 border rounded-md p-4">
      <div>
        <Label htmlFor="c-key">Key</Label>
        <Input id="c-key" value={key} onChange={(e) => setKey(e.target.value)} placeholder="my-server" required />
      </div>
      <div>
        <Label htmlFor="c-label">Label</Label>
        <Input id="c-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="My CMS" />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="c-url">Base URL</Label>
        <Input id="c-url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://example.com/api/v1.0" required />
      </div>
      <div>
        <Label htmlFor="c-type">Type</Label>
        <select id="c-type" className="w-full border rounded-md h-9 px-2 text-sm" value={type} onChange={(e) => setType(e.target.value)}>
          {supportedTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <Label htmlFor="c-cols">Remote collections (comma-separated)</Label>
        <Input id="c-cols" value={collections} onChange={(e) => setCollections(e.target.value)} placeholder="pages, posts, people" />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="c-token">Token (dtoken) — optional, write-only</Label>
        <Input id="c-token" type="password" value={dtoken} onChange={(e) => setDtoken(e.target.value)} placeholder="•••• stored encrypted" autoComplete="new-password" />
      </div>
      <div className="md:col-span-2">
        <Button type="submit" disabled={saving}>{saving ? "Creating…" : "Add connector"}</Button>
      </div>
    </form>
  );
}

function ConnectorCard({
  connector,
  supportedTypes,
  onChanged,
  onError,
}: {
  connector: Connector;
  supportedTypes: string[];
  onChanged: (msg?: string) => void;
  onError: (msg: string) => void;
}) {
  const [label, setLabel] = useState(connector.label);
  const [baseUrl, setBaseUrl] = useState(connector.baseUrl);
  const [type, setType] = useState(connector.type);
  const [enabled, setEnabled] = useState(connector.enabled);
  const [collections, setCollections] = useState((connector.config?.collections || []).join(", "));
  const [newToken, setNewToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingFieldTypes, setSavingFieldTypes] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [importName, setImportName] = useState("");
  const [importCollection, setImportCollection] = useState((connector.config?.collections || [])[0] || "");
  const [fieldTypes, setFieldTypes] = useState<ConnectorFieldTypeOverrides>(
    () => normalizeFieldTypes(connector.config?.fieldTypes)
  );
  const [fieldTypesCollection, setFieldTypesCollection] = useState(
    (connector.config?.collections || [])[0] || ""
  );
  const [collectionFields, setCollectionFields] = useState<ConnectorCollectionField[]>([]);
  const [collectionFieldsLoading, setCollectionFieldsLoading] = useState(false);
  const [collectionFieldsError, setCollectionFieldsError] = useState<string | null>(null);

  const tokenSet = Boolean(connector.secretsSet?.dtoken);
  const declaredCollections = parseCollections(collections);
  const fieldTypesDirty = useMemo(
    () =>
      fieldTypesKey(fieldTypes) !==
      fieldTypesKey(normalizeFieldTypes(connector.config?.fieldTypes)),
    [fieldTypes, connector.config?.fieldTypes]
  );

  useEffect(() => {
    setFieldTypes(normalizeFieldTypes(connector.config?.fieldTypes));
  }, [connector.uid, connector.config?.fieldTypes]);

  useEffect(() => {
    if (!declaredCollections.length) {
      setFieldTypesCollection("");
      return;
    }
    if (!declaredCollections.includes(fieldTypesCollection)) {
      setFieldTypesCollection(declaredCollections[0]);
    }
  }, [declaredCollections.join("|"), fieldTypesCollection]);

  useEffect(() => {
    if (!fieldTypesCollection || !declaredCollections.includes(fieldTypesCollection)) {
      setCollectionFields([]);
      setCollectionFieldsError(null);
      return;
    }

    let cancelled = false;
    setCollectionFieldsLoading(true);
    setCollectionFieldsError(null);
    void connectorsAPI
      .collectionFields(connector.key, fieldTypesCollection)
      .then((res) => {
        if (cancelled) return;
        setCollectionFields(res.fields);
      })
      .catch((err) => {
        if (cancelled) return;
        setCollectionFields([]);
        setCollectionFieldsError(err instanceof Error ? err.message : "Failed to load fields");
      })
      .finally(() => {
        if (!cancelled) setCollectionFieldsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [connector.key, fieldTypesCollection, declaredCollections.join("|")]);

  const save = async () => {
    setSaving(true);
    try {
      await connectorsAPI.update(connector.key, {
        label, baseUrl, type, enabled,
        config: {
          ...connector.config,
          collections: declaredCollections,
        },
      });
      if (newToken.trim()) {
        await connectorsAPI.setSecret(connector.key, newToken.trim());
        setNewToken("");
      }
      onChanged("Connector saved");
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to save connector");
    } finally {
      setSaving(false);
    }
  };

  const saveFieldTypes = async () => {
    setSavingFieldTypes(true);
    try {
      await connectorsAPI.update(connector.key, {
        config: {
          ...connector.config,
          collections: declaredCollections,
          fieldTypes: pruneFieldTypes(fieldTypes),
        },
      });
      onChanged("Field types saved");
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to save field types");
    } finally {
      setSavingFieldTypes(false);
    }
  };

  const runTest = async () => {
    setTestResult("Testing…");
    try {
      const res = await connectorsAPI.test(connector.key);
      setTestResult(res.ok ? "✓ Connection OK" : `✗ ${res.error || "Failed"}`);
    } catch (err) {
      setTestResult(`✗ ${err instanceof Error ? err.message : "Failed"}`);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete connector "${connector.key}"? Imported collections are not removed.`)) return;
    try {
      await connectorsAPI.remove(connector.key);
      onChanged("Connector deleted");
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to delete connector");
    }
  };

  const doImport = async () => {
    if (!importCollection || !importName.trim()) {
      onError("Pick a remote collection and a name to import");
      return;
    }
    try {
      const res = await connectorsAPI.importCollection(connector.key, {
        remoteCollection: importCollection,
        name: importName.trim(),
      });
      setImportName("");
      onChanged(`Customized "${importCollection}" (${res.inferredFields} fields). Configure access in ACL admin.`);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to import collection");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center">
            <Server className="w-4 h-4 mr-2" /> {connector.label}{" "}
            <span className="ml-2 text-xs text-gray-500 font-normal">({connector.key})</span>
          </span>
          <span className="flex items-center gap-2">
            <Badge variant={enabled ? "default" : "secondary"}>{enabled ? "Enabled" : "Disabled"}</Badge>
            <Badge variant={tokenSet ? "default" : "destructive"}>{tokenSet ? "Token configured ✓" : "Token not set"}</Badge>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div>
            <Label>Type</Label>
            <select className="w-full border rounded-md h-9 px-2 text-sm" value={type} onChange={(e) => setType(e.target.value)}>
              {supportedTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <Label>Base URL</Label>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>Remote collections (comma-separated)</Label>
            <Input value={collections} onChange={(e) => setCollections(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>Rotate token (leave blank to keep current)</Label>
            <Input type="password" value={newToken} onChange={(e) => setNewToken(e.target.value)} placeholder="•••• leave blank to keep" autoComplete="new-password" />
          </div>
          <div className="flex items-center gap-2">
            <input id={`en-${connector.uid}`} type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <Label htmlFor={`en-${connector.uid}`} className="!mb-0">Enabled</Label>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          <Button variant="outline" onClick={runTest}>Test connection</Button>
          <Button variant="outline" asChild>
            <Link to={`/admin/connectors/${encodeURIComponent(connector.key)}/files`}>
              <Images className="w-4 h-4 mr-1" /> Files
            </Link>
          </Button>
          {testResult && <span className="text-sm">{testResult}</span>}
          <Button variant="destructive" onClick={remove} className="ml-auto">
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        </div>

        <div className="border-t pt-4 space-y-3">
          <Label className="flex items-center"><ListTree className="w-4 h-4 mr-1" /> Field types</Label>
          <p className="text-xs text-gray-500">
            Override inferred field types for live connector collections. For example, set HTML body fields to rich text instead of plain text.
          </p>
          {declaredCollections.length === 0 ? (
            <p className="text-xs text-gray-500">Add remote collection names above and Save first.</p>
          ) : (
            <>
              <div>
                <Label className="text-xs">Remote collection</Label>
                <select
                  className="border rounded-md h-9 px-2 text-sm w-full max-w-xs"
                  value={fieldTypesCollection}
                  onChange={(e) => setFieldTypesCollection(e.target.value)}
                >
                  {declaredCollections.map((rc) => (
                    <option key={rc} value={rc}>{rc}</option>
                  ))}
                </select>
              </div>
              {collectionFieldsLoading && (
                <p className="text-sm text-gray-500">Loading fields…</p>
              )}
              {collectionFieldsError && (
                <p className="text-sm text-red-600">{collectionFieldsError}</p>
              )}
              {!collectionFieldsLoading && !collectionFieldsError && collectionFields.length > 0 && (
                <div className="rounded-md border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500">
                      <tr>
                        <th className="px-3 py-2 font-medium">Field</th>
                        <th className="px-3 py-2 font-medium">Inferred</th>
                        <th className="px-3 py-2 font-medium">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collectionFields.map((field) => {
                        const overrideType = fieldTypes[fieldTypesCollection]?.[field.name];
                        const effectiveType = overrideType || field.inferredType;
                        return (
                          <tr key={field.name} className="border-t">
                            <td className="px-3 py-2">
                              <div className="font-medium">{field.label}</div>
                              <div className="text-xs text-gray-500">{field.name}</div>
                            </td>
                            <td className="px-3 py-2 text-gray-600">{field.inferredType}</td>
                            <td className="px-3 py-2">
                              <select
                                className="border rounded-md h-8 px-2 text-sm w-full min-w-[8rem]"
                                value={effectiveType}
                                onChange={(e) => {
                                  setFieldTypes((current) =>
                                    setFieldOverride(
                                      current,
                                      fieldTypesCollection,
                                      field.name,
                                      field.inferredType,
                                      e.target.value
                                    )
                                  );
                                }}
                              >
                                <option value={field.inferredType}>
                                  Auto ({field.inferredType})
                                </option>
                                {CONNECTOR_FIELD_TYPE_OPTIONS.filter((t) => t !== field.inferredType).map((t) => (
                                  <option key={t} value={t}>{t}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {!collectionFieldsLoading && !collectionFieldsError && collectionFields.length === 0 && fieldTypesCollection && (
                <p className="text-xs text-gray-500">No fields found in a sample of remote items.</p>
              )}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button
                  onClick={saveFieldTypes}
                  disabled={savingFieldTypes || saving || !fieldTypesDirty}
                >
                  {savingFieldTypes ? "Saving…" : "Save field types"}
                </Button>
                {fieldTypesDirty && (
                  <span className="text-xs text-amber-700">Unsaved field type changes</span>
                )}
              </div>
            </>
          )}
        </div>

        <div className="border-t pt-4">
          <Label className="flex items-center"><Download className="w-4 h-4 mr-1" /> Optional: customize in Labwick</Label>
          <p className="text-xs text-gray-500 mt-1 mb-2">
            Remote collections appear in the sidebar automatically and read/write live data.
            Import only if you want a custom name, menu folder, or field layout stored in Labwick.
          </p>
          <div className="flex flex-wrap items-end gap-2 mt-2">
            <div>
              <Label className="text-xs">Remote collection</Label>
              <select className="border rounded-md h-9 px-2 text-sm" value={importCollection} onChange={(e) => setImportCollection(e.target.value)}>
                <option value="">— select —</option>
                {declaredCollections.map((rc) => <option key={rc} value={rc}>{rc}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">New collection name</Label>
              <Input value={importName} onChange={(e) => setImportName(e.target.value)} placeholder="Pages" />
            </div>
            <Button variant="outline" onClick={doImport}>Customize</Button>
          </div>
          {declaredCollections.length === 0 && (
            <p className="text-xs text-gray-500 mt-1">Add remote collection names above and Save first to enable import.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function parseCollections(value: string): string[] {
  return value.split(",").map((s) => s.trim()).filter(Boolean);
}

function normalizeFieldTypes(input?: ConnectorFieldTypeOverrides): ConnectorFieldTypeOverrides {
  if (!input || typeof input !== "object") return {};
  return Object.fromEntries(
    Object.entries(input).map(([collection, fields]) => [
      collection,
      fields && typeof fields === "object" ? { ...fields } : {},
    ])
  );
}

function fieldTypesKey(input: ConnectorFieldTypeOverrides): string {
  const pruned = pruneFieldTypes(input);
  const collections = Object.keys(pruned).sort();
  const normalized = Object.fromEntries(
    collections.map((collection) => [
      collection,
      Object.fromEntries(
        Object.entries(pruned[collection]).sort(([a], [b]) => a.localeCompare(b))
      ),
    ])
  );
  return JSON.stringify(normalized);
}

function pruneFieldTypes(input: ConnectorFieldTypeOverrides): ConnectorFieldTypeOverrides {
  const out: ConnectorFieldTypeOverrides = {};
  Object.entries(input).forEach(([collection, fields]) => {
    if (!collection || !fields || typeof fields !== "object") return;
    const cleaned = Object.fromEntries(
      Object.entries(fields).filter(([, type]) => typeof type === "string" && type.trim())
    );
    if (Object.keys(cleaned).length) out[collection] = cleaned;
  });
  return out;
}

function setFieldOverride(
  current: ConnectorFieldTypeOverrides,
  collection: string,
  fieldName: string,
  inferredType: string,
  nextType: string
): ConnectorFieldTypeOverrides {
  const out = { ...current };
  const collectionOverrides = { ...(out[collection] || {}) };
  if (!nextType || nextType === inferredType) {
    delete collectionOverrides[fieldName];
  } else {
    collectionOverrides[fieldName] = nextType;
  }
  if (Object.keys(collectionOverrides).length) {
    out[collection] = collectionOverrides;
  } else {
    delete out[collection];
  }
  return out;
}
